import type { ReactNode } from 'react';
import React, { createContext, useContext } from 'react'

import _ from 'lodash'


import { useLock } from '@/shared/hooks/useLock'
import { usePersistedGamificationPages } from '@/shared/hooks/usePersistedGamificationPages'
import { CountdownPage } from '@/shared/ui/Countdown'
import type { Config } from '@/types/config'
import type { DigitalProduct } from '@/types/digitalProduct'
import type { GamificationPage } from '@/types/gamificationPage'

import type { DateTime } from 'luxon'

export interface DigitalProductContextValue {
  /** Event slug used in API paths — not the numeric `DigitalProduct.id`. */
  eventSlug: string
  /**
   * @deprecated Use `eventSlug`. String slug for `/user/digital_products/{slug}/`.
   */
  digitalProductId: string
  /**
   * Ausente durante o boot, enquanto o shell já está montado e a área de
   * conteúdo mostra o skeleton. Quem monta nesse intervalo — o chrome — deve
   * tratar a ausência; código de feature só renderiza depois, por construção
   * do `Guard`.
   */
  digitalProduct?: DigitalProduct
  refetch: () => void
  config: Config
  /** Menu do bottom nav, configurado pelo produtor. Fonte única — não refetch em `useNavigation`. */
  pages: GamificationPage[]
  /**
   * `false` enquanto os itens reais são desconhecidos. Distinto de `pages`
   * vazio, que é configuração legítima do produtor.
   */
  pagesKnown: boolean
  lockRules: {
    isAllowedModule: (slug: string) => boolean
    byPass: boolean
    status: string
    startAt: DateTime
    endAt: DateTime
  }
  basePathWithId: string
  [key: string]: unknown
}

interface DigitalProductProviderProps {
  digitalProductId: string
  digitalProduct?: DigitalProduct
  refetch: () => void
  config: Config
  children: ReactNode
}

const DigitalProductContext = createContext<DigitalProductContextValue | null>(
  null
)

/**
 * Lança apenas quando o PROVIDER falta — isso é erro de programação.
 *
 * NÃO lança quando o evento ainda não chegou, e a distinção custou caro: a
 * primeira versão lançava nos dois casos, para garantir que feature nunca
 * visse meio dado. O efeito real foi outro. O provider monta desde o primeiro
 * frame para o shell não remontar, e a partir daí QUALQUER um dos 331 módulos
 * alcançáveis da árvore de boot que chamasse este hook derrubava o app inteiro
 * com tela branca — seis o faziam, três a mais que os encontrados por busca.
 *
 * A garantia de que conteúdo de feature não renderiza sem evento é estrutural
 * e mora no `Guard`: a área de conteúdo mostra o skeleton da rota até o dado
 * chegar. É uma decisão de renderização, e é lá que ela pertence — trocá-la
 * por uma exceção no acesso ao dado espalhou a responsabilidade por toda a
 * árvore e não sobreviveu ao primeiro smoke-teste.
 */
const useDigitalProductContext = (): DigitalProductContextValue => {
  const ctx = useContext(DigitalProductContext)
  if (!ctx)
    throw new Error(
      'useDigitalProductContext must be used within DigitalProductProvider'
    )
  return ctx
}

/**
 * Versão que não lança quando o evento ainda não chegou.
 *
 * Existe para o chrome — topbar e bottomnav montam ANTES dos dados, em
 * superfícies neutras, para que a altura fique estável do primeiro frame ao
 * último e o conteúdo abaixo não salte quando o nome e o avatar entram.
 *
 * Devolve `null` quando não há provider, em vez de lançar.
 *
 * Serve a quem monta fora de uma árvore com provider garantido, e a quem
 * prefere tratar a ausência a propagar exceção. Para ramificar no estado de
 * carregando — como a topbar faz para escolher entre placeholder e conteúdo —
 * o que interessa é `ctx?.digitalProduct`, não a existência do provider.
 */
const useDigitalProductContextOptional =
  (): DigitalProductContextValue | null => useContext(DigitalProductContext)

const DigitalProductProvider = ({
  digitalProductId,
  digitalProduct,
  refetch,
  config,
  children,
  ...rest
}: DigitalProductProviderProps) => {
  const basePathWithId = ''
  // Do bundle persistido quando existem; o REST revalida. É o que põe os itens
  // do produtor no primeiro frame da barra.
  const { pages, known: pagesKnown } = usePersistedGamificationPages()
  const [isAllowedModule, byPass, status, startAt, endAt] = useLock(
    { ...digitalProduct, pages },
    basePathWithId as string
  )

  // O provider monta desde o primeiro frame, com ou sem evento, para que o
  // shell não remonte quando os dados chegam. A trava de contagem regressiva
  // só pode decidir depois que há dado — antes disso não há `start_at` para
  // comparar, e bloquear por ausência mostraria contagem a quem não devia.
  const aguardandoEvento = !digitalProduct

  if (aguardandoEvento || status === 'open' || byPass)
    return (
      <DigitalProductContext.Provider
        value={{
          eventSlug: digitalProductId,
          digitalProductId,
          digitalProduct,
          refetch,
          config: {
            ...config,
            apiBasePath: `/user/digital_products/${digitalProductId}`,
            basePathWithId
          },
          basePathWithId,
          pages,
          pagesKnown,
          lockRules: {
            isAllowedModule,
            byPass,
            status,
            startAt,
            endAt
          },
          ...rest
        }}
      >
        {children}
      </DigitalProductContext.Provider>
    )
  return (
    <CountdownPage
      status={status}
      digitalProduct={digitalProduct}
      startAt={startAt}
      onComplete={refetch}
    />
  )
}

export {
  DigitalProductContext,
  useDigitalProductContext,
  useDigitalProductContextOptional,
  DigitalProductProvider
}
